import { useEffect } from 'react';
import { Phone, ArrowRight, Shield, Clock, Users } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export const CtaSection = () => {
  useEffect(() => {
    (function (C, A, L) {
      let p = function (a, ar) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "business-consultation", { origin: "https://app.cal.com" });

    window.Cal.ns["business-consultation"]("inline", {
      elementOrSelector: "#my-cal-inline-business-consultation",
      config: { layout: "month_view", useSlotsViewOnSmallScreen: "true" },
      calLink: "roofconverse.ai/business-consultation",
    });

    window.Cal.ns["business-consultation"]("ui", {
      hideEventTypeDetails: false,
      layout: "month_view",
    });
  }, []);

  const benefits = [
    { icon: Clock, text: "15-minute strategy call" },
    { icon: Shield, text: "No obligation, no pressure" },
    { icon: Users, text: "Speak directly with our team" }
  ];

  return (
    <section id="cta-section" className="py-20 md:py-28 bg-slate-900" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Side */}
          <div className="text-center lg:text-left">
            <p className="text-orange-500 font-semibold text-sm uppercase tracking-wider mb-4">
              Ready to Stop Leaving Money on the Table?
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              Let's Talk About Your Lead Flow
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              Book a brief strategy call. We'll analyze your current lead response process, identify the gaps costing you jobs, and show you exactly how our system would work for your business.
            </p>
            
            {/* Benefits List */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center lg:justify-start">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-300">
                  <benefit.icon className="w-5 h-5 text-orange-500" />
                  <span>{benefit.text}</span>
                </div>
              ))}
            </div>

            {/* What to Expect */}
            <Card className="bg-slate-800 border-slate-700 rounded-sm">
              <CardContent className="p-6">
                <h4 className="text-white font-heading text-lg font-semibold mb-4">
                  What Happens on the Call:
                </h4>
                <ol className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <span className="bg-orange-600 text-white text-xs font-bold w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                    <span>We'll review your current lead volume and response process</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-orange-600 text-white text-xs font-bold w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                    <span>We'll identify where leads are falling through the cracks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-orange-600 text-white text-xs font-bold w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                    <span>If we're a fit, we'll outline a custom implementation plan</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>

          {/* Cal.com Embed Side */}
          <div>
            <Card className="bg-white rounded-sm shadow-xl" data-testid="cal-embed-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-200 pb-4">
                  <div className="w-10 h-10 rounded bg-orange-100 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900">
                      Schedule Your Strategy Call
                    </h3>
                    <p className="text-slate-500 text-sm">Choose a time that works for you</p>
                  </div>
                </div>
                
                {/* Cal.com Inline Embed */}
                <div
                  id="my-cal-inline-business-consultation"
                  style={{ width: "100%", height: "100%", overflow: "auto", minHeight: "500px" }}
                  data-testid="cal-embed"
                ></div>
              </CardContent>
            </Card>

            {/* Trust Note */}
            <p className="text-center text-slate-400 text-sm mt-6">
              We respect your time. This call is about seeing if we can help, not a hard sell.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;
